/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { atom, useRecoilValue, useSetRecoilState } from "recoil";
import sanitizeHTML from "sanitize-html";
import { TempCampaignAtom } from "../../../../atoms/CampaignAtom";
import { UserAtom } from "../../../../atoms/UserAtom";
import { CampaignValidation } from "../../../../pages/create-campaign";
import { getBody } from "../../../../pages/new-email";
import { INode } from "../../../../types/CampaignTypes";
import { IOption } from "../../../../types/CommonTypes";
import { LEARNISTIC_UID } from "../../../../utils/constants";
import { localStorageGetItem } from "../../../../utils/LocalStorage";
import EmailEditor, { EditorHandle } from "../../../EmailEditor";
import IconBerserkerMail from "../../../Icon";
import IconButton from "../../../IconButton";
import PreviewEmail from "../../../Modal/container/PreviewEmail";
import { useModal } from "../../../Modal/Modal";
import StyledAutocompleteSelect from "../../../StyledAutoCompleteSelect/AutoCompleteSelect";
import Delay from "./Delay";
import Form, { DefaultFormProps, FormInstance } from "./Form";

const Container = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-size: 14px;
  font-weight: 600;
  display: flex;
`;

const PreviewButton = styled.div`
  width: 100px;
  height: 42px;
`;

const Footer = styled.div`
  color: #2f2f2f;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  padding: 0px 30px 30px;
`;

const PreviewModalTitle = styled.div`
  font-size: 18px;
`;

export function validateEmail(node: INode): CampaignValidation {
  const newBody = sanitizeHTML(node.email?.body || "", {
    allowedTags: [],
  }).trim();

  const userUidFromLocalStorage = localStorageGetItem("USER_UID");
  const hasLearnisticLink = node.email.body.includes("learnistic.me");
  if (hasLearnisticLink && LEARNISTIC_UID !== userUidFromLocalStorage) {
    return {
      node: null,
      error: {
        toastInfo: {
          toastText: "Learnistic.me deep links cannot be sent directly.",
          toastType: "info",
        },
      },
    };
  }
  if (
    newBody &&
    node.email.subject &&
    node.email.footerId &&
    node.email.aliasId
  ) {
    return {
      node,
      error: null,
    };
  } else {
    return {
      node: null,
      error: {
        toastInfo: {
          toastText: "All fields are required",
          toastType: "info",
        },
      },
    };
  }
}

export const ChangeAtom = atom<boolean>({
  key: "ChangeAtom",
  default: true,
});

const Email = ({ action, nodeIndex }: DefaultFormProps): JSX.Element => (
  <Form {...{ action, nodeIndex }}>
    {({ updateCampaign, tempCampaign }: FormInstance) => {
      const setTempCampaign = useSetRecoilState(TempCampaignAtom);
      const user = useRecoilValue(UserAtom);
      const editorHandlerRef = useRef<EditorHandle>(null);
      const titleContainerRef = useRef<HTMLDivElement | null>(null);

      const modal = useModal();

      const currentNode = tempCampaign?.nodes[nodeIndex];

      const footerOptions = user.emailOptions.footers.map((f) => ({
        value: f.id as string,
        name: f.name as string,
      }));

      const aliasOptions = user.emailOptions.aliases.map((a) => ({
        value: a.id as string,
        name: `${a.fromName} <${a.address}>`,
      }));

      const editor = editorHandlerRef.current?.getEditor();

      const campaignEmail = currentNode?.email;

      const [bottomBorder, setBottomBorder] = useState<boolean>(false);

      function placeBottomBorder() {
        const titleContainterRect =
          titleContainerRef.current.getBoundingClientRect();
        const titleContainterParentRect =
          titleContainerRef.current.parentElement.getBoundingClientRect();

        setBottomBorder(
          titleContainterParentRect.top === titleContainterRect.top
        );
      }

      useEffect(() => {
        if (!titleContainerRef.current) return null;
        titleContainerRef.current.parentElement.addEventListener(
          "scroll",
          placeBottomBorder
        );
        return () => {
          titleContainerRef.current?.parentElement.removeEventListener(
            "scroll",
            placeBottomBorder
          );
        };
      }, []);

      function saveForm() {}

      const selectedFooter = useMemo(
        () =>
          (footerOptions.find(
            (o) => o.value === campaignEmail?.footerId
          ) as any) || footerOptions[0],
        footerOptions
      );

      const selectedAlias = useMemo(
        () =>
          (aliasOptions.find(
            (o) => o.value === campaignEmail?.aliasId
          ) as any) || aliasOptions[0],
        aliasOptions
      );

      const setSelectedFooter = (option: IOption) => {
        setTempCampaign((tempCampaign) => ({
          ...tempCampaign,
          // @ts-ignore
          nodes: [
            ...tempCampaign.nodes.slice(0, nodeIndex),
            {
              ...currentNode,
              email: {
                ...currentNode.email,
                footerId: option.value,
              },
            },
            ...tempCampaign.nodes.slice(nodeIndex + 1),
          ],
        }));
      };

      const setSelectedAlias = (option: IOption) => {
        setTempCampaign((tempCampaign) => ({
          ...tempCampaign,
          // @ts-ignore
          nodes: [
            ...tempCampaign.nodes.slice(0, nodeIndex),
            {
              ...currentNode,
              email: {
                ...currentNode.email,
                aliasId: option.value,
              },
            },
            ...tempCampaign.nodes.slice(nodeIndex + 1),
          ],
        }));
      };

      if (!campaignEmail) return { Component: null, saveForm };

      const Component = (
        <Container>
          <Delay {...{ action, nodeIndex, tempCampaign }} />
          <Title
            ref={titleContainerRef}
            css={css`
              padding: 20px 30px 0px;
              /* fixed keyword is fine too */
              position: sticky;
              top: 0;
              z-index: 3;
              background-color: white;

              /* z-index works pretty much like a layer:
              the higher the z-index value, the greater
              it will allow the navigation tag to stay on top
              of other tags */
            `}
          >
            <div
              css={css`
                justify-content: space-between;
                display: flex;
                width: 100%;
                padding-bottom: 20px;
                ${bottomBorder
                  ? css`
                      border-bottom: 1px solid #dddddd;
                    `
                  : css`
                      border-bottom: none;
                    `}
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <IconBerserkerMail
                  name="message"
                  width={16}
                  height={12}
                  color="#2F2F2F"
                />
                &nbsp;EMAIL
              </div>
              <PreviewButton>
                <IconButton
                  css={css`
                    opacity: ${campaignEmail.body && campaignEmail.subject
                      ? 1
                      : 0.5};
                    cursor: ${campaignEmail.body && campaignEmail.subject
                      ? "pointer"
                      : "auto"};
                    pointer-events: ${campaignEmail.body &&
                    campaignEmail.subject
                      ? "auto"
                      : "none"};
                  `}
                  onClick={() =>
                    modal.pushModal(
                      <PreviewEmail
                        subject={campaignEmail.subject as string}
                        body={getBody(editor, user)}
                        user={user!}
                        editor={editor}
                        emailInfo={{
                          footer: user?.emailOptions.footers.find(
                            (f) => f.id === selectedFooter.value
                          ),
                          alias: user?.emailOptions.aliases.find(
                            (f) => f.id === selectedAlias.value
                          ),
                        }}
                        isCampaign
                      />,
                      <PreviewModalTitle>Preview</PreviewModalTitle>
                    )
                  }
                  icon={"preview"}
                >
                  Preview
                </IconButton>
              </PreviewButton>
            </div>
          </Title>
          <div
            css={css`
              padding: 0 30px 30px;

              > div {
                overflow-x: auto;
              }

              #subject {
                padding: ${user.emailOptions.splitTesting &&
                "0 0 5px !important"};
              }
            `}
          >
            <EmailEditor
              mode="campaign"
              ref={editorHandlerRef}
              index={nodeIndex}
              campaignLength={tempCampaign.nodes.length}
            />
          </div>
          <Footer>
            <div
              css={css`
                margin-bottom: 5px;
              `}
            >
              Footer:&nbsp;&nbsp;
            </div>
            <StyledAutocompleteSelect
              disabled={!selectedFooter}
              selectedOption={selectedFooter}
              onOptionSelect={(option) => {
                if (option.value) setSelectedFooter(option);
              }}
              placeholder="Select a Footer"
              options={footerOptions}
              noTimer
            />
          </Footer>
          <Footer>
            <div
              css={css`
                margin-bottom: 5px;
              `}
            >
              Send From:&nbsp;&nbsp;
            </div>
            <StyledAutocompleteSelect
              disabled={!selectedAlias}
              selectedOption={selectedAlias}
              onOptionSelect={(option) => {
                if (option.value) setSelectedAlias(option);
              }}
              placeholder="Select a Send From"
              options={aliasOptions}
              noTimer
            />
          </Footer>
        </Container>
      );

      return {
        Component,
        saveForm,
      };
    }}
  </Form>
);

export default Email;
